import { useEffect, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 8 — Pendulum Exercise (Eighth-Note Variations).
 *
 * Linear stepwise practice in BOTH directions — up and down from the tonic.
 * Symmetric versions (1↑1↓, 2↑2↓, 3↑3↓, 4↑4↓) build out into asymmetric
 * versions (2↑1↓, 1↑2↓, 3↑2↓ …). Every note is an eighth; a full swing is
 * 2×(up+down) of them, so the symmetric shapes land on whole bars and the
 * asymmetric ones cycle against the bar instead of resolving with it.
 *
 * Bespoke visual: a literal animated pendulum SVG. Pick a variation; the
 * pendulum swings the right number of steps in each direction.
 */

interface Variation {
  id: string;
  up: number;
  down: number;
  label: string;
  rhythm: string; // a tiny mnemonic
}
/* A full swing is 2×(up + down) eighth notes. The symmetric ones land on whole
   bars; the asymmetric ones deliberately don't, which is what makes them feel
   like they're pulling against the pulse. */
const VARIATIONS: Variation[] = [
  { id: "1u1d", up: 1, down: 1, label: "1 up · 1 down",  rhythm: "4 eighths · half a bar" },
  { id: "2u2d", up: 2, down: 2, label: "2 up · 2 down",  rhythm: "8 eighths · one full bar" },
  { id: "3u3d", up: 3, down: 3, label: "3 up · 3 down",  rhythm: "12 eighths · a bar and a half" },
  { id: "4u4d", up: 4, down: 4, label: "4 up · 4 down",  rhythm: "16 eighths · two full bars" },
  { id: "2u1d", up: 2, down: 1, label: "2 up · 1 down",  rhythm: "6 eighths · 3 beats — pulls against the bar" },
  { id: "1u2d", up: 1, down: 2, label: "1 up · 2 down",  rhythm: "6 eighths · 3 beats — the same, flipped" },
  { id: "3u2d", up: 3, down: 2, label: "3 up · 2 down",  rhythm: "10 eighths · 5 beats" },
  { id: "4u2d", up: 4, down: 2, label: "4 up · 2 down",  rhythm: "12 eighths · 6 beats" },
];

export default function PianoIntermediate8() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="E" availableQualities={["major", "harmonic-minor"]}>
        <PIShell
          title="Pendulum Exercise"
          exerciseGoal="Stepwise motion in both directions from the tonic. Practice the y-axis. Swing your hand like a clock pendulum across asymmetric beats."
          bpm={92}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [varIdx, setVarIdx] = useState(0);
  const variation = VARIATIONS[varIdx];

  // Build the pendulum sequence as scale-degree offsets, where positive = up
  // from tonic, negative = down. Pivot is always the tonic.
  const computeSemis = (deg: number): number => {
    // For positive degrees: ascend the scale.
    // For negative degrees: descend the scale (each step uses scale intervals).
    const intervals = k.quality === "major"
      ? [0, 2, 4, 5, 7, 9, 11]      // major
      : [0, 2, 3, 5, 7, 8, 11];     // harmonic minor
    if (deg === 0) return 0;
    let pos = 0;
    let s = 0;
    if (deg > 0) {
      for (let i = 0; i < deg; i++) {
        const next = intervals[(pos + 1) % 7];
        const cur  = intervals[pos];
        const step = next > cur ? next - cur : (next + 12) - cur;
        s += step;
        pos = (pos + 1) % 7;
      }
    } else {
      for (let i = 0; i < -deg; i++) {
        const prevPos = (pos - 1 + 7) % 7;
        const cur  = intervals[pos];
        const prev = intervals[prevPos];
        const step = cur >= prev ? cur - prev : (cur + 12) - prev;
        s -= step;
        pos = prevPos;
      }
    }
    return s;
  };

  /**
   * One full swing of the pendulum, in scale degrees either side of the tonic.
   *
   *   0 → +1 … +up → … +1 → 0 → −1 … −down → … −1  ↺
   *
   * It starts ON the pivot, reaches the furthest note in BOTH directions, and
   * comes back — so the notes lit on the keyboard above are exactly the notes
   * you hear. (The previous version began on +1, stopped one step short of the
   * lowest note, and for "1 up · 1 down" played the tonic twice in a row.)
   */
  const buildDegrees = (): number[] => {
    const { up, down } = variation;
    const seq: number[] = [0];
    for (let i = 1; i <= up; i++) seq.push(i);       // climb
    for (let i = up - 1; i >= 1; i--) seq.push(i);   // back down to just above the pivot
    seq.push(0);                                     // through the pivot
    for (let i = 1; i <= down; i++) seq.push(-i);    // descend
    for (let i = down - 1; i >= 1; i--) seq.push(-i); // back up to just below the pivot
    return seq;                                      // loops straight into the next 0
  };

  const BPM = 92;

  const buildEvents = () => {
    const eighth = 60 / BPM / 2;
    return buildDegrees().map((deg, i) => ({
      time: i * eighth,
      notes: [midiToPitchName(tonicMidi + computeSemis(deg), k.useFlats)],
      duration: "8n",
    }));
  };

  const playVariation = async () => {
    kickAudio();
    await playSequence(buildEvents(), BPM);
  };

  // Master keyboard: light up tonic, plus the swing range for the active variation
  const masterHighlights: KeyHighlight[] = [];
  for (let d = -variation.down; d <= variation.up; d++) {
    if (d === 0) {
      masterHighlights.push({ pc: k.tonicPc, tone: "root", label: "Sa" });
    } else {
      const semis = computeSemis(d);
      const pc = ((k.tonicPc + semis) % 12 + 12) % 12;
      masterHighlights.push({ pc, tone: d > 0 ? "voice" : "bass", label: d > 0 ? `+${d}` : `${d}` });
    }
  }

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The notes the pendulum swings between"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Gold = the pivot ("Sa", the tonic). Cyan = upward steps. Pink =
          downward steps below the tonic.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="A practice for the y-axis">
          <p>
            Most scale practice marches in one direction. The pendulum is{" "}
            <strong>linear motion in both directions from a pivot</strong> —
            the tonic stays still and the rest of your hand swings up and
            down around it. It builds finger crossings, scale fluency, and
            (most importantly) compositional vocabulary for stepwise melody.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "The pendulum, what it allows us to do is to go in both directions,
            sort of like you're looking at both parts of the y-axis if you
            think about it, the positive y and the negative y."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Pick a variation" title="Eight ways to swing">
          <p>
            Choose one. The pendulum below animates the swing visually; the
            ▶ button plays it in your current key, in even eighth notes.
            Symmetric variations (e.g. 3 up / 3 down) come out to a whole
            number of bars. Asymmetric ones (e.g. 2 up / 1 down) don't — their
            cycle runs against the bar, which is the point, and Drill 3 shows
            you what to do about it.
          </p>
          <div className="my-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            {VARIATIONS.map((v, i) => {
              const active = i === varIdx;
              const isAsym = v.up !== v.down;
              return (
                <motion.button
                  key={v.id}
                  layout
                  onClick={() => setVarIdx(i)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div className={`text-[13px] font-bold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                    {v.label}
                  </div>
                  <div className="mt-0.5 text-[11px] text-cyan-300/70">{v.rhythm}</div>
                  {isAsym && (
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-rose-300/80">
                      asymmetric
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
          <PendulumSwing variation={variation} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playVariation}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play "{variation.label}" in {k.tonicName}
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Asymmetric rhythm trick" title="Stretch two notes to fill the bar">
          <p>
            "2 up · 1 down" is a six-note swing — 0 +1 +2 +1 0 −1 — which is
            six eighth notes, or three beats. That won't fill a 4/4 bar. So
            <strong> lengthen two of the six into quarter notes</strong> and
            the cycle lands exactly on four beats. This is yours to do at the
            keyboard, not the player's: pick <em>which</em> two you stretch —
            at the front, in the middle, at the end — and the same melodic
            shape gives you a fistful of different rhythms.
          </p>
          <p>
            Jason's point: confining yourself to a mini prison — you just
            have to make it fit 4/4 — can actually bring out your creative
            potential.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Once you've got the pendulum in both directions, once you've
            tried out all the rhythm patterns which are there for you to try,
            then you need to make it your own."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Crossing-over" title="Use your thumb intelligently">
          <p>
            The pendulum is a great way to practise crossing your fingers,
            because the thumb is the pivot on the tonic. Going up, the fingers
            simply walk. Going below the tonic, a finger crosses over the
            thumb — the middle finger for 2 down (or the index, then bring the
            thumb back), the ring or the middle for 3 and 4 down. Aim for
            legato — no audible gap between notes.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Left hand" title="Don't leave it idle">
          <p>
            While the right swings, the left should do something rhythmic.
            Suggested options:
          </p>
          <ul className="my-3 space-y-1 text-[14px] text-cyan-50/95">
            <li>· A steady tonic pulse (one note per beat).</li>
            <li>· Root–octave toggle.</li>
            <li>· A "We Will Rock You"-style boom-boom-clap.</li>
            <li>· A walking <strong>1–6–4–5</strong> bass line under the swing.</li>
          </ul>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The full pendulum loop">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="8"
            label={`Pendulum · ${k.tonicName} ${k.quality === "major" ? "major" : "minor"}`}
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Twelve minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Start symmetric, end asymmetric. Once asymmetric variations
                feel natural, you've unlocked compositional vocabulary —
                this is how stepwise melodies start sounding original.
              </>
            }
            rungs={[
              { title: "1↑1↓ and 2↑2↓",  minutes: 3, accent: "amber",   detail: "Symmetric. Eighth notes only. Establish the pivot — your thumb returns to the tonic on every cycle." },
              { title: "3↑3↓ and 4↑4↓",  minutes: 3, accent: "cyan",    detail: "Symmetric. The swing is wider — your fingers cross. Slow it down if the legato breaks." },
              { title: "Asymmetric (2↑1↓ / 1↑2↓)", minutes: 3, accent: "rose",   detail: "Lengthen the right notes to fit 4/4. Vocalise the rhythm before you play it." },
              { title: "3↑2↓ + your own combinations", minutes: 3, accent: "emerald", detail: "Make up an asymmetric pattern that fits 4/4. Add a left-hand pulse under it." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: a literal animated pendulum SVG ── */
function PendulumSwing({ variation }: { variation: Variation }) {
  const length = 110;
  const cx = 160;
  const cy = 30;
  // The maximum upward swing maps to up to about -75° from vertical;
  // downward to +75°. We're picking ranges to reflect step count visually.
  const stepDeg = 14; // degrees per scale step
  const maxLeft = -variation.up * stepDeg;
  const maxRight = variation.down * stepDeg;
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<"left" | "right">("right");

  // Toggle phase periodically
  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(
      () => setPhase((p) => (p === "left" ? "right" : "left")),
      1100
    );
    return () => window.clearInterval(id);
  }, [reduced]);

  const angle = phase === "left" ? maxLeft : maxRight;
  const tipX = cx + length * Math.sin((angle * Math.PI) / 180);
  const tipY = cy + length * Math.cos((angle * Math.PI) / 180);

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-cyan-950/20 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ⊢ {variation.label}
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          swing · {variation.up} ↑ &nbsp;·&nbsp; {variation.down} ↓
        </div>
      </div>
      <svg viewBox="0 0 320 180" className="w-full">
        {/* Top mounting */}
        <circle cx={cx} cy={cy} r="4" fill="#F59E0B" />
        {/* Swing range arc */}
        <path
          d={`M ${cx + length * Math.sin((maxLeft * Math.PI) / 180)} ${cy + length * Math.cos((maxLeft * Math.PI) / 180)} A ${length} ${length} 0 0 1 ${cx + length * Math.sin((maxRight * Math.PI) / 180)} ${cy + length * Math.cos((maxRight * Math.PI) / 180)}`}
          stroke="rgba(34,211,238,0.25)"
          strokeWidth="2"
          fill="none"
        />
        {/* Vertical reference */}
        <line x1={cx} y1={cy} x2={cx} y2={cy + length} stroke="rgba(148,163,184,0.2)" strokeDasharray="3 3" />
        {/* Pendulum arm */}
        <motion.line
          x1={cx}
          y1={cy}
          animate={{ x2: tipX, y2: tipY }}
          transition={{ duration: 1.0, ease: "easeInOut" }}
          stroke="#67E8F9"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {/* Bob */}
        <motion.circle
          animate={{ cx: tipX, cy: tipY }}
          transition={{ duration: 1.0, ease: "easeInOut" }}
          r="9"
          fill="url(#pendBob)"
          stroke="#0E7490"
          strokeWidth="2"
        />
        {/* Step markers along the arc */}
        {Array.from({ length: variation.up }, (_, i) => {
          const a = -((i + 1) * stepDeg);
          const x = cx + (length + 8) * Math.sin((a * Math.PI) / 180);
          const y = cy + (length + 8) * Math.cos((a * Math.PI) / 180);
          return <text key={`u${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fontFamily="JetBrains Mono,monospace" fill="#67E8F9">{`+${i + 1}`}</text>;
        })}
        {Array.from({ length: variation.down }, (_, i) => {
          const a = (i + 1) * stepDeg;
          const x = cx + (length + 8) * Math.sin((a * Math.PI) / 180);
          const y = cy + (length + 8) * Math.cos((a * Math.PI) / 180);
          return <text key={`d${i}`} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="10" fontFamily="JetBrains Mono,monospace" fill="#FB7185">{`-${i + 1}`}</text>;
        })}
        <defs>
          <radialGradient id="pendBob">
            <stop offset="0%" stopColor="#FCD34D" />
            <stop offset="100%" stopColor="#D97706" />
          </radialGradient>
        </defs>
      </svg>
      <p className="mt-1 text-center text-[12px] italic text-cyan-300/70">
        Tonic at the top; cyan = upward steps; pink = downward steps. The
        bob just slowly toggles between the two extremes — the music sweeps
        through every step in between.
      </p>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
